/* eslint-disable prettier/prettier */
import React, { useMemo, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom'; // 💡 Import useLocation and useNavigate
import { useUserProfile } from '../../hooks/useUserProfile';
import Sidebar, { NavItem, ProfileData } from './Sidebar';
import DashboardHeader from './DashboardHeader';
import styles from '../../styles/layout/MainLayout.module.css';
import iocmLogo from '../../assets/images/IOCM_logo.png';
import styless from '../../styles/layout/Sidebar.module.css';

// 💡 Import all your icons
import { ReactComponent as DashboardIcon } from '../../assets/icons/dashboard.svg';
import { ReactComponent as CoursesIcon } from '../../assets/icons/courses.svg';
import { ReactComponent as ScheduleIcon } from '../../assets/icons/schedule.svg';
import { ReactComponent as NotesIcon } from '../../assets/icons/notes.svg';
import { ReactComponent as AnalyticsIcon } from '../../assets/icons/analytics.svg';
import { ReactComponent as HelpIcon } from '../../assets/icons/helpSupport.svg';
import { ReactComponent as SettingsIcon } from '../../assets/icons/settings.svg';
import { ReactComponent as MessagesIcon } from '../../assets/icons/message.svg';
import { ReactComponent as UsersIcon } from '../../assets/icons/activeCoaches.svg';
import { ReactComponent as ProgramsIcon } from '../../assets/icons/ProgramsIcon.svg';
import { ReactComponent as ManageUsersIcon } from '../../assets/Schedule/manageUsers.svg';
import MobileBottomNav from './MobileBottomNav';

// --- Reusable Navigation Item Objects ---
const dashboardItem: NavItem = {
	id: 'dashboard',
	label: 'Dashboard',
	icon: (
		<div className={styles.sidebarItem}>
			<DashboardIcon className={styless.sideBarIcon} />
		</div>
	),
};
const programItem: NavItem = {
	id: 'programs',
	label: 'Programs',
	icon: (
		<div className={styles.sidebarItem}>
			<ProgramsIcon className={styless.sideBarIcon} />
		</div>
	),
};
const scheduleItem: NavItem = {
	id: 'schedule',
	label: 'Schedule',
	icon: (
		<div className={styles.sidebarItem}>
			<ScheduleIcon className={styless.sideBarIcon} />
		</div>
	),
};
const notesItem: NavItem = {
	id: 'notes',
	label: 'Notes',
	icon: (
		<div className={styles.sidebarItem}>
			<NotesIcon className={styless.sideBarIcon} />
		</div>
	),
};
const analyticsItem: NavItem = {
	id: 'analytics',
	label: 'Analytics',
	icon: (
		<div className={styles.sidebarItem}>
			<AnalyticsIcon className={styless.sideBarIcon} />
		</div>
	),
};

const manageUsersItem: NavItem = {
	id: 'manageUsers',
	label: 'Manage Users',
	icon: <ManageUsersIcon />,
};

// --- Navigation Arrays for Each Role ---
const adminNavItems: NavItem[] = [
	dashboardItem,
	programItem,
	scheduleItem,
	manageUsersItem,
	notesItem,
	analyticsItem,
];

const masterCoachNavItems: NavItem[] = [
	dashboardItem,
	programItem,
	scheduleItem,
	{ id: 'coachDashboard', label: 'Coach Dashboard', icon: <UsersIcon /> },
	manageUsersItem,
	notesItem,
	analyticsItem,
];

const coachMentorNavItems: NavItem[] = [
	dashboardItem,
	programItem,
	scheduleItem,
	manageUsersItem,
	notesItem,
	analyticsItem,
];

const learnerNavItems: NavItem[] = [
	dashboardItem,
	programItem,
	scheduleItem,
	notesItem,
	analyticsItem,
];

const secondaryNavItems: NavItem[] = [
	{
		id: 'messages',
		label: 'Messages',
		icon: (
			<div className={styles.sidebarItem}>
				<MessagesIcon className={styless.sideBarIcon} />
			</div>
		),
	},
	{
		id: 'help',
		label: 'Help & Support',
		icon: (
			<div className={styles.sidebarItem}>
				<HelpIcon className={styless.sideBarIcon} />
			</div>
		),
	},
	{
		id: 'settings',
		label: 'Settings',
		icon: (
			<div className={styles.sidebarItem}>
				<SettingsIcon className={styless.sideBarIcon} />
			</div>
		),
	},
];

const MainLayout: React.FC = () => {
	const [isMobile, setIsMobile] = useState(false);
	//  State to manage Mobile Sidebar visibility
	const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
	const location = useLocation();
	const isGuestView = location.pathname.includes('guest'); // Get the current URL location
	const navigate = useNavigate();

	const { profile } = useUserProfile();
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);

		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	const navItems = useMemo(() => {
		const role = profile?.rolename?.toLowerCase();
		let baseItems: NavItem[] = [];

		if (role === 'admin' || role === 'superadmin') {
			baseItems = adminNavItems;
		} else if (role === 'master coach') {
			baseItems = masterCoachNavItems;
		} else if (role === 'coach' || role === 'coach mentor') {
			baseItems = coachMentorNavItems;
		} else {
			baseItems = learnerNavItems;
		}

		return baseItems.map(item => {
			let shouldDisable = false;

			// 🔹 Guest → only Dashboard active
			if (role === 'guest') {
				shouldDisable = item.id !== 'dashboard';
			}

			// 🔹 Super Admin → only Programs & Manage Users
			else if (role === 'superadmin') {
				shouldDisable =
					item.id !== 'dashboard' &&
					item.id !== 'programs' &&
					item.id !== 'manageUsers';
			}

			// 🔹 Coach permissions
			else if (role === 'coach' || role === 'coach mentor') {
				shouldDisable =
					item.id !== 'dashboard' &&
					item.id !== 'programs' &&
					item.id !== 'manageUsers';
			}

			// 🔹 Learner / Coachee permissions
			else if (role === 'learner' || role === 'coachee') {
				shouldDisable =
					item.id !== 'dashboard' && item.id !== 'programs';
			}

			return {
				...item,
				isDisabled: shouldDisable,
				subItems: item.subItems?.map(sub => ({
					...sub,
					isDisabled: shouldDisable,
				})),
			};
		});
	}, [profile]);

	const dynamicSecondaryNav = useMemo(() => {
		// const isGuest = location.pathname.includes('guest');

		return secondaryNavItems.map(item => ({
			...item,
			isDisabled: true, // Everything in secondary (Settings, Help, etc.) is disabled
		}));
	}, []);

	const userName = profile?.fullname
		? profile.fullname.split(' ')[0]
		: 'John';

	const roleName = profile?.rolename || 'Coachee';

	// This data would ideally come from a user context or an auth hook
	const userProfile: ProfileData = {
		name: userName || 'John',
		role: roleName,
		imageUrl: 'https://randomuser.me/api/portraits/men/1.jpg',
		completionPercent: 0,
	};

	const handleNavClick = (id: string) => {
		console.log(`Navigating to: ${id}`);

		const clickedItem = [...navItems, ...dynamicSecondaryNav].find(
			item => item.id === id
		);
		// const basePath = location.pathname.split('/')[1];

		// If the user clicks "Dashboard", navigate to the root of that section
		if (clickedItem?.isDisabled) {
			console.log(`Navigation blocked for disabled item: ${id}`);
			return;
		}

		const basePath = location.pathname.split('/')[1];
		if (id === 'dashboard') {
			navigate(`/${basePath}`);
		} else {
			navigate(`/${basePath}/${id}`);
		}

		// Close sidebar on mobile after clicking
		setIsMobileSidebarOpen(false);
	};

	const filteredNavItems = useMemo(() => {
		if (!isMobile) return navItems;

		// 🚫 Remove Dashboard & Programs from sidebar on mobile
		return navItems.filter(
			item => item.id !== 'dashboard' && item.id !== 'programs'
		);
	}, [navItems, isMobile]);

	return (
		<div className={styles.layout}>
			<Sidebar
			profile={userProfile}
				logoSrc={iocmLogo}
				navItems={filteredNavItems} // 💡 Use the dynamically selected nav items
				secondaryNavItems={dynamicSecondaryNav} // Secondary items are the same for all roles
				
				onNavItemClick={handleNavClick}
				isOpen={isMobileSidebarOpen}
				onClose={() => setIsMobileSidebarOpen(false)}
			/>

			{/* Dark Overlay for Mobile */}
			{isMobileSidebarOpen && (
				<div
					className={styles.mobileOverlay}
					onClick={() => setIsMobileSidebarOpen(false)}
				/>
			)}

			<div className={styles.contentColumn}>
				<DashboardHeader
					onMenuClick={() => setIsMobileSidebarOpen(true)}
				/>
				<main className={styles.scrollableArea}>
					<Outlet />
				</main>
				{/* NEW: Bottom Navigation Bar */}
				<MobileBottomNav />
			</div>
		</div>
	);
};

export default MainLayout;
